import { useProfileContext } from '@contexts/profile'
import { AVATAR_IMAGE } from '@lib/constants/gamification'
import React from 'react'
import { Linkedin, MapPin } from 'react-feather'

// Teachers enter total experience as free text — "10+ Years", "5 yrs", or
// sometimes a bare number like "1". Append a unit only when the value has no
// letters, so existing entries that already spell out the unit aren't doubled.
const formatExperience = (raw: string) => {
  const value = raw.trim()
  if (!value) return ''
  if (/[a-zA-Z]/.test(value)) return value
  const leadingNumber = parseFloat(value)
  const unit = leadingNumber === 1 ? 'year' : 'years'
  return `${value} ${unit}`
}

const TeacherDetails = () => {
  const { profileDetails } = useProfileContext()
  const user = profileDetails
  const teacher = profileDetails?.teacher

  if (!teacher) return null

  const avatarSrc = user?.avatar || AVATAR_IMAGE.UNSPECIFIED_AVATAR

  return (
    <section className="flex flex-col">
      {/* Cover banner — same pattern as student profile, branded for teachers */}
      <div className="relative h-40 md:h-56 w-full overflow-hidden rounded-t-lg">
        <img
          src="/images/student-cover-images/student_profile_banner.png?v=2"
          alt="Cover"
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-black opacity-20 pointer-events-none" />
      </div>

      <div className="flex flex-col lg:flex-row relative">
        {/* Avatar — overlapping the cover, locked to a perfect circle */}
        <div className="flex items-center justify-center lg:justify-start lg:w-1/4 px-5 lg:px-8 relative z-10">
          <div className="-mt-20 lg:-mt-24 h-40 w-40 lg:h-48 lg:w-48 flex-shrink-0 rounded-full bg-white shadow-md p-1">
            <img
              src={avatarSrc}
              alt={user?.name || 'Teacher'}
              className="block w-full h-full rounded-full object-cover bg-grey-100"
            />
          </div>
        </div>

        <div className="flex flex-col space-y-3 p-5 lg:py-6 lg:pr-8 leading-tight text-grey-800 w-full">
          <div>
            <h1 className="text-2xl lg:text-3xl font-700 break-all">
              {user?.name}
            </h1>
            <p className="text-orange font-600 mt-1 text-lg">
              {teacher.subtitle}
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            {teacher.totalExperienceYears && (
              <div>
                <p className="text-grey-700 text-xs uppercase tracking-wide">
                  Experience
                </p>
                <p className="font-600 mt-1">
                  {formatExperience(teacher.totalExperienceYears)}
                </p>
              </div>
            )}
            {teacher.location && (
              <div>
                <p className="text-grey-700 text-xs uppercase tracking-wide">
                  Location
                </p>
                <p className="font-600 mt-1 flex items-center">
                  <MapPin size={14} className="mr-1 text-orange" />
                  {teacher.location}
                </p>
              </div>
            )}
            {teacher.linkedinUrl && (
              <div>
                <p className="text-grey-700 text-xs uppercase tracking-wide">
                  LinkedIn
                </p>
                <a
                  className="font-600 mt-1 text-orange hover:underline focus:outline-none flex items-center"
                  href={teacher.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin size={14} className="mr-1" />
                  View profile
                </a>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default React.memo(TeacherDetails)
